import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api'

export type WearableProviderStatus =
  | 'pending'
  | 'active'
  | 'syncing'
  | 'revoked'
  // OAuth grant is unrecoverably dead (refresh token revoked/expired). Terminal:
  // the backend stops polling it and the UI prompts a reconnect.
  | 'needs_reauth'
  | 'error'

export interface WearableProviderInfo {
  name: string
  display: string
  status: 'supported' | 'coming_soon'
}

export interface WearableIntegration {
  provider: string
  provider_display?: string | null
  status: WearableProviderStatus
  connected_at: string | null
  last_sync_at: string | null
  backfill_completed_at: string | null
  last_error: string | null
}

interface ProvidersResponse {
  providers: WearableProviderInfo[]
}

interface StatusResponse {
  integrations: WearableIntegration[]
}

interface AuthorizeResponse {
  auth_url: string
}

export function useWearableProviders() {
  return useQuery({
    queryKey: ['wearables', 'providers'],
    queryFn: () => api.get<ProvidersResponse>('/api/wearables/providers'),
    staleTime: 5 * 60_000,
  })
}

export function useWearableStatus() {
  return useQuery({
    queryKey: ['wearables', 'status'],
    queryFn: () => api.get<StatusResponse>('/api/wearables/status'),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data as StatusResponse | undefined
      const anySyncing = data?.integrations?.some((i) => i.status === 'syncing' || i.status === 'pending')
      return anySyncing ? 5000 : false
    },
  })
}

export function useConnectWearable() {
  return useMutation({
    mutationFn: async (provider: string) => {
      const res = await api.get<AuthorizeResponse>(`/api/wearables/${provider}/authorize`)
      window.location.assign(res.auth_url)
      return res
    },
  })
}

export function useDisconnectWearable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (provider: string) => api.post(`/api/wearables/${provider}/disconnect`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wearables'] })
    },
  })
}

export function useSyncWearable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (provider: string) => api.post(`/api/wearables/${provider}/sync`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wearables', 'status'] })
    },
  })
}

export function useBackfillWearable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { provider: string; days: number }) =>
      api.post(`/api/wearables/${args.provider}/backfill`, { days: args.days }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wearables', 'status'] })
      qc.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}

export interface WearableSeriesPoint {
  date: string
  hrv: number | null
  rhr: number | null
  readiness: number | null
  sleep_min: number | null
  steps: number | null
}

export interface WearableLastNight {
  hrv_rmssd_ms: number | null
  resting_hr_bpm: number | null
  sleep_total_minutes: number | null
  sleep_efficiency: number | null
  readiness_score: number | null
  stress_score: number | null
  steps: number | null
}

export interface WearableBaseline {
  hrv_rmssd_ms: number | null
  resting_hr_bpm: number | null
  sleep_total_minutes: number | null
  readiness_score: number | null
}

export interface WearableInsightsResponse {
  connected: boolean
  provider?: string
  provider_display?: string
  last_sync_at?: string | null
  status?: WearableProviderStatus
  last_night_date?: string | null
  last_night?: WearableLastNight | null
  baseline_7d?: WearableBaseline
  series?: WearableSeriesPoint[]
  days_with_data?: number
  signal?: string | null
}

export function useWearableInsights(enabled = true) {
  return useQuery({
    queryKey: ['wearables', 'insights'],
    queryFn: () => api.get<WearableInsightsResponse>('/api/wearables/insights'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useReestimatePain() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { provider: string; days: number }) =>
      api.post<{
        ok: boolean
        days_estimated: number
        days_skipped_user: number
        days_no_signal: number
      }>(`/api/wearables/${args.provider}/reestimate`, { days: args.days }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
      qc.invalidateQueries({ queryKey: ['patient', 'patterns'] })
    },
  })
}
